import { useMemo, useState } from 'react';
import { Box, Button, Collapse } from '@mui/material';
import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { splitEmailBodyQuotes } from './utils/threadDisplayUtils';

const emailBodySx = {
  typography: 'body2',
  lineHeight: 1.65,
  color: 'text.primary',
  '& img': { maxWidth: '100%', height: 'auto' },
  '& a': { color: 'primary.main' },
  '& p': { m: 0, mb: 1 },
  '& p:last-child': { mb: 0 },
  wordBreak: 'break-word',
  '& .gmail_attr': {
    color: 'text.secondary',
    mb: 0.5,
  },
  '& .gmail_quote': {
    mt: 1,
  },
  '& blockquote, & blockquote.gmail_quote': {
    m: '0 0 0 0.8ex',
    borderLeft: '1px solid',
    borderColor: 'divider',
    pl: '1ex',
    color: 'text.secondary',
  },
} as const;

type FinanceEmailCollapsibleBodyProps = {
  html: string;
  /** Reset collapse when the parent message card collapses / message changes. */
  resetKey?: string;
};

/**
 * Renders email HTML with Gmail-style collapsible quoted section
 * (gmail_quote / blockquote / "On … wrote:").
 */
export const FinanceEmailCollapsibleBody = ({
  html,
  resetKey,
}: FinanceEmailCollapsibleBodyProps) => {
  const [quotesOpen, setQuotesOpen] = useState(false);
  const resetToken = `${resetKey ?? ''}\0${html}`;
  const [prevResetToken, setPrevResetToken] = useState(resetToken);
  const { mainHtml, quoteHtml } = useMemo(() => splitEmailBodyQuotes(html), [html]);

  if (resetToken !== prevResetToken) {
    setPrevResetToken(resetToken);
    setQuotesOpen(false);
  }

  if (!quoteHtml) {
    return <Box sx={emailBodySx} dangerouslySetInnerHTML={{ __html: html }} />;
  }

  return (
    <Box>
      {mainHtml ? <Box sx={emailBodySx} dangerouslySetInnerHTML={{ __html: mainHtml }} /> : null}

      <Box sx={{ my: mainHtml ? 1 : 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<MoreHorizIcon fontSize="small" />}
          onClick={(event) => {
            event.stopPropagation();
            setQuotesOpen((open) => !open);
          }}
          aria-expanded={quotesOpen}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
            borderColor: 'divider',
            bgcolor: 'action.hover',
            fontSize: 12,
            py: 0.25,
            px: 1,
            minHeight: 28,
            '&:hover': {
              borderColor: 'text.disabled',
              bgcolor: 'action.selected',
            },
          }}
        >
          {quotesOpen ? 'Hide quoted text' : 'Show quoted text'}
        </Button>
      </Box>

      <Collapse in={quotesOpen} timeout="auto" unmountOnExit={false}>
        <Box sx={emailBodySx} dangerouslySetInnerHTML={{ __html: quoteHtml }} />
      </Collapse>
    </Box>
  );
};
